import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import api from '../../lib/api';
import { resolveImageUrl } from '../../lib/imageUrl';
import { useText } from '../../lib/siteContent';

const NAV_LINKS = [
  { name: 'La Maison', href: '/la-maison', testId: 'nav-la-maison' },
  { name: 'Nos Univers', href: '/#univers', testId: 'nav-nos-univers' },
  { name: 'Réalisations', href: '/realisations', testId: 'nav-realisations' },
  { name: 'Contact', href: '/contact', testId: 'nav-contact' },
  { name: 'Demander un devis', href: '/devis', testId: 'nav-devis' },
];

const Logo = ({ src, light }) => {
  const t = useText();
  return (
  <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo">
    {src ? (
      <img src={resolveImageUrl(src, 480)} alt="JBMH Events" className={`h-9 md:h-11 w-auto max-w-[180px] object-contain transition-[filter] duration-700 ${light ? 'brightness-0 invert' : ''}`} />
    ) : (
      <span className={`font-serif text-xl md:text-2xl tracking-[0.18em] uppercase transition-colors duration-700 ${light ? 'text-white' : 'text-[#111111]'}`}>{t('nav.logo')}</span>
    )}
  </Link>
  );
};

const Navbar = ({ overlay = false }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [atTop, setAtTop] = React.useState(overlay);
  React.useEffect(() => {
    if (!overlay) return undefined;
    const on = () => setAtTop(window.scrollY < window.innerHeight - 120);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, [overlay]);
  const light = overlay && atTop;
  const [logo, setLogo] = React.useState(null);
  const location = useLocation();

  React.useEffect(() => {
    api.get('/site-photos/logo')
      .then(({ data }) => setLogo(data.images?.[0] || null))
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }
    const scroll = () => document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth' });
    const timers = [150, 900, 1800].map((d) => setTimeout(scroll, d));
    return () => timers.forEach(clearTimeout);
  }, [location]);

  React.useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    document.documentElement.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; document.documentElement.style.overflow = ''; };
  }, [isOpen]);

  return (
    <nav className={`${overlay ? 'fixed inset-x-0' : 'sticky'} top-0 z-50 border-b transition-[background-color,border-color] duration-700 ${light ? 'bg-transparent border-transparent' : 'bg-[#FFFFFF] border-[#111111]/10'}`} data-testid="main-navbar">
      <div className="container-custom flex justify-between items-center h-20 md:h-24">
        <Logo src={logo} light={light} />

        <div className="hidden lg:flex items-center gap-10 xl:gap-14">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              data-testid={link.testId}
              className={`relative group py-2 text-[11px] uppercase tracking-[0.28em] font-normal transition-colors duration-700 ${light ? 'text-white' : 'text-[#111111]'}`}
            >
              {link.name}
              <span className={`absolute left-0 -bottom-0.5 h-px w-0 transition-[width] duration-500 group-hover:w-full ${light ? 'bg-white' : 'bg-[#111111]'}`} />
            </Link>
          ))}
        </div>

        <button
          className={`lg:hidden p-2 -mr-2 transition-colors duration-700 ${light ? 'text-white' : 'text-[#111111]'}`}
          onClick={() => setIsOpen(true)}
          aria-label="Ouvrir le menu"
          data-testid="nav-mobile-toggle"
        >
          <Menu size={24} strokeWidth={1.25} />
        </button>
      </div>

      {isOpen && (
        <div className="fixed inset-0 bg-[#FFFFFF] z-50 flex flex-col animate-fade-in" data-testid="nav-mobile-menu">
          <div className="container-custom w-full flex justify-between items-center h-20 border-b border-[#111111]/10">
            <Logo src={logo} />
            <button className="text-[#111111] p-2 -mr-2" onClick={() => setIsOpen(false)} aria-label="Fermer le menu" data-testid="nav-mobile-close">
              <X size={24} strokeWidth={1.25} />
            </button>
          </div>
          <div className="container-custom w-full flex flex-col gap-8 pt-16">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                data-testid={`${link.testId}-mobile`}
                className="text-sm uppercase tracking-[0.3em] text-[#111111]"
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
